import { readGailApproval, type MutationProposal } from "./mutationProposal";

export type GailToolStatus = "running" | "completed" | "error";

export type GailToolCall = {
  id: string;
  name: string;
  status: GailToolStatus;
  input?: unknown;
  output?: unknown;
  approval?: { id: string; approved?: boolean; proposal?: MutationProposal; previewUnavailable?: true };
};

export type ToolTranslateFn = (key: string, options?: Record<string, unknown>) => string;

const STREAM_TOOL_TYPES = new Set([
  "tool-input-start",
  "tool-input-delta",
  "tool-input-available",
  "tool-input-error",
  "tool-output-available",
  "tool-output-error",
  "tool-output-denied",
  "tool-approval-request",
  "tool-approval-response",
]);

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;
}

function textField(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function upsert(calls: GailToolCall[], next: GailToolCall): GailToolCall[] {
  const index = calls.findIndex((call) => call.id === next.id);
  const merged: GailToolCall = index === -1
    ? { id: next.id, name: next.name, status: next.status }
    : {
        ...calls[index],
        name: next.name || calls[index].name,
        status: calls[index].status === "completed" && next.status === "running" ? calls[index].status : next.status,
      };
  if (next.input !== undefined) merged.input = next.input;
  if (next.output !== undefined) merged.output = next.output;
  if (next.approval !== undefined) merged.approval = { ...(merged.approval?.id === next.approval.id ? merged.approval : {}), ...next.approval };
  if (index === -1) return [...calls, merged];
  return calls.map((call, callIndex) => (callIndex === index ? merged : call));
}

function withPayload(call: GailToolCall, record: Record<string, unknown>, fields: Array<"input" | "output">): GailToolCall {
  const next = { ...call };
  for (const field of fields) {
    const value = toolPayload(record, field);
    if (value !== undefined) next[field] = value;
  }
  return next;
}
function toolPayload(record: Record<string, unknown>, key: "input" | "output"): unknown {
  if (key === "input") return record.input !== undefined ? record.input : record.parameters;
  return record.output !== undefined ? record.output : record.result;
}

/** Collapse one UI-message chunk or stored part into a single tool call. */
export function applyToolRecord(calls: GailToolCall[], record: Record<string, unknown>): GailToolCall[] {
  const type = textField(record, "type") ?? "";
  if (type === "tool-input-delta") return calls;

  if (type === "data-gailApproval") {
    const binding = readGailApproval(record.data);
    const existing = calls.find((call) => call.id === binding.toolCallId);
    return upsert(calls, {
      id: binding.toolCallId, name: existing?.name ?? "tool", status: existing?.status ?? "running",
      approval: { id: binding.approvalId, proposal: binding.proposal, previewUnavailable: binding.previewUnavailable },
    });
  }
  if (type === "tool-approval-request" || type === "tool-approval-response") {
    const id = textField(record, "approvalId");
    if (!id) return calls;
    const existing = calls.find((call) => call.id === record.toolCallId || call.approval?.id === id);
    if (!existing) return calls;
    return upsert(calls, { ...existing, approval: { ...existing.approval, id,
      ...(typeof record.approved === "boolean" ? { approved: record.approved } : {}),
    } });
  }

  if (type === "tool-input-start" || type === "tool-input-available" || type === "tool-input-error") {
    const id = textField(record, "toolCallId");
    if (!id) return calls;
    return upsert(calls, withPayload({
      id,
      name: textField(record, "toolName") ?? "tool",
      status: type === "tool-input-error" ? "error" : "running",
    }, record, ["input"]));
  }

  if (type === "tool-output-available" || type === "tool-output-error" || type === "tool-output-denied") {
    const id = textField(record, "toolCallId");
    if (!id) return calls;
    const existing = calls.find((call) => call.id === id);
    return upsert(calls, withPayload({
      id,
      name: textField(record, "toolName") ?? existing?.name ?? "tool",
      status: type === "tool-output-available" ? "completed" : "error",
    }, record, ["output"]));
  }

  if (type === "dynamic-tool" || (type.startsWith("tool-") && !STREAM_TOOL_TYPES.has(type))) {
    const id = textField(record, "toolCallId");
    if (!id) return calls;
    const state = textField(record, "state");
    const status: GailToolStatus =
      state === "output-error" || state === "output-denied"
        ? "error"
        : state === "output-available"
          ? "completed"
          : "running";
    const name = type === "dynamic-tool" ? textField(record, "toolName") ?? "tool" : type.slice("tool-".length);
    const approval = asRecord(record.approval);
    return upsert(calls, withPayload({ id, name, status,
      ...(typeof approval?.id === "string" ? { approval: { id: approval.id,
        ...(typeof approval.approved === "boolean" ? { approved: approval.approved } : {}),
      } } : {}),
    }, record, ["input", "output"]));
  }

  if (type === "function_call" || type === "functionCall") {
    const name = textField(record, "functionName") ?? textField(record, "name") ?? "tool";
    const id = textField(record, "callId") ?? textField(record, "id") ?? name;
    return upsert(calls, withPayload({ id, name, status: "running" }, record, ["input"]));
  }

  if (type === "function_result") {
    const id = textField(record, "callId");
    if (!id) return calls;
    const existing = calls.find((call) => call.id === id);
    return upsert(calls, withPayload({
      id,
      name: textField(record, "functionName") ?? existing?.name ?? "tool",
      status: "completed",
    }, record, ["output"]));
  }

  return calls;
}

/** Walk a stream chunk or stored message and fold every tool part into one row per call. */
export function collectToolCalls(value: unknown, calls: GailToolCall[] = []): GailToolCall[] {
  const record = asRecord(value);
  if (!record) return calls;
  let next = applyToolRecord(calls, record);
  for (const key of ["parts", "content", "contents", "message"]) {
    const child = record[key];
    if (Array.isArray(child)) {
      for (const item of child) next = collectToolCalls(item, next);
    } else if (child) {
      next = collectToolCalls(child, next);
    }
  }
  return next;
}

/** Middle segment of `provider_get_resource`, matching the web fallback label. */
export function extractToolName(functionName: string): string | null {
  const parts = functionName.split("_");
  if (parts.length < 2) return null;
  const middlePart = parts.length >= 3 ? parts[1] : parts.at(-1);
  return middlePart ? middlePart.charAt(0).toUpperCase() + middlePart.slice(1) : null;
}

export function describeToolCall(
  functionName: string,
  completed: boolean,
  t: ToolTranslateFn,
): { icon: string; label: string } {
  const name = functionName || "";
  if (name.includes("WebSearchPlugin") || name.includes("web_search")) {
    return { icon: "globe", label: t(completed ? "tools.phrases.searchedWeb" : "tools.phrases.searchingWeb") };
  }
  if (name === "search_docs") {
    return { icon: "magnifying-glass", label: t(completed ? "tools.phrases.searchedDocs" : "tools.phrases.searchingDocs") };
  }
  if (name === "read_doc_page") {
    return { icon: "book", label: t(completed ? "tools.phrases.readDocs" : "tools.phrases.readingDocs") };
  }
  if (
    name.includes("execute_python") ||
    name.includes("execute_command") ||
    name.includes("execute_code") ||
    name.includes("CodeExecutionPlugin")
  ) {
    return { icon: "terminal", label: t(completed ? "tools.phrases.ranCode" : "tools.phrases.runningCode") };
  }
  const toolName = extractToolName(name);
  if (completed) {
    return {
      icon: "wrench",
      label: toolName ? t("tools.phrases.usedTool", { tool: toolName }) : t("tools.phrases.usedGenericTool"),
    };
  }
  return {
    icon: "wrench",
    label: toolName ? t("tools.phrases.usingTool", { tool: toolName }) : t("tools.phrases.usingGenericTool"),
  };
}
